import React from "react";
import {
  DecisionPageLayout,
  DecisionSection,
} from "@/components/DecisionPageLayout";
import { InlineRelatedLinks } from "@/components/RelatedPages";
import CompareTool from "./compare-treatments/CompareTool";
import { TREATMENTS } from "./compare-treatments/treatmentData";
import { compareTreatmentsJsonLd } from "./compare-treatments/schema";

const CompareTreatments: React.FC = () => {
  return (
    <DecisionPageLayout
      seo={{
        title:
          "Compare Depression Treatments: SAINT® vs TMS vs Spravato® vs Ketamine vs ECT",
        description:
          "Side-by-side comparison of SAINT®, standard TMS, SPRAVATO®, ketamine and ECT for treatment-resistant depression: time, evidence, side effects, cost and insurance.",
        canonical: "/compare-treatments",
        keywords:
          "tms vs saint vs spravato vs ketamine, compare depression treatments, saint vs spravato, saint vs ketamine, best treatment for treatment resistant depression",
        breadcrumbs: [
          { name: "Home", href: "/" },
          { name: "Compare Treatments", href: "/compare-treatments" },
        ],
        jsonLd: compareTreatmentsJsonLd,
      }}
      badge="Treatment Comparison"
      title="Compare Treatments for Treatment-Resistant Depression"
      subtitle="No single treatment is best for everyone. Use the interactive tool to compare SAINT®, standard TMS, SPRAVATO®, ketamine and ECT side by side — then see if SAINT® is a fit, free."
      reviewedBy="Daniel Pietras, MD"
      lastUpdated="2026-11-03"
      ctaHeading="See if SAINT® is a fit, free"
      ctaText="No commitment. We'll review your situation, answer your questions, and help you understand whether SAINT® or another treatment is the right fit. One phone call — 954-827-9793."
    >
      {/* Quick answer */}
      <DecisionSection>
        <div className="rounded-xl border border-accent/30 bg-accent/5 p-4 sm:p-5">
          <p className="text-base sm:text-lg text-foreground/90 leading-relaxed">
            There is no single best treatment for treatment-resistant
            depression. The right choice depends on how quickly you need relief,
            how much time you can spend in clinic, your tolerance for side
            effects, and your insurance. SAINT® delivers the fastest reported
            remission in the fewest clinic days; SPRAVATO® and ketamine act
            quickly but often need ongoing maintenance; standard TMS and ECT
            take weeks but are widely covered. Use the tool below to compare two
            or three side by side.
          </p>
        </div>
      </DecisionSection>

      {/* Table of contents */}
      <DecisionSection>
        <nav className="rounded-xl border border-border bg-card p-4 text-sm">
          <p className="font-semibold text-primary mb-2">On this page</p>
          <ul className="space-y-1 text-foreground/80">
            <li>
              <a
                href="#interactive-tool"
                className="text-accent hover:underline"
              >
                The interactive comparison tool
              </a>
            </li>
            <li>
              <a href="#how-to-read" className="text-accent hover:underline">
                How to read the comparison
              </a>
            </li>
            <li>
              <a
                href="#when-considered"
                className="text-accent hover:underline"
              >
                When each option is usually considered
              </a>
            </li>
            <li>
              <a href="#one-roof" className="text-accent hover:underline">
                All five under one roof at Neuro Wellness
              </a>
            </li>
            <li>
              <a
                href="#comparison-table"
                className="text-accent hover:underline"
              >
                Full comparison table
              </a>
            </li>
            <li>
              <a href="#faq" className="text-accent hover:underline">
                FAQ
              </a>
            </li>
            <li>
              <a href="#cited-research" className="text-accent hover:underline">
                Cited research
              </a>
            </li>
          </ul>
        </nav>
      </DecisionSection>

      {/* Interactive tool */}
      <DecisionSection heading="The interactive comparison tool">
        <div id="interactive-tool">
          <CompareTool />
        </div>
      </DecisionSection>

      {/* How to read */}
      <DecisionSection heading="How to read the comparison">
        <div id="how-to-read">
          <p>
            <strong>FDA status</strong> tells you whether a treatment has been
            formally cleared or approved for depression. "Off-label" means the
            drug is FDA-approved but not specifically for depression.
          </p>
          <p>
            <strong>Time commitment</strong> is how many days or weeks you need
            to be in the clinic. SAINT® compresses treatment into five
            consecutive days; standard TMS and ECT spread sessions over weeks.
          </p>
          <p>
            <strong>Time to response</strong> is how quickly people typically
            notice a change. "Response" means a meaningful improvement;{" "}
            <strong>remission</strong> means symptoms drop below the threshold
            for a depression diagnosis.
          </p>
          <p>
            <strong>Cost / coverage</strong> is a general guide. Coverage varies
            by plan; we verify your benefits for free before you commit to
            anything.
          </p>
        </div>
      </DecisionSection>

      {/* When each is considered */}
      <DecisionSection heading="When each option is usually considered">
        <div id="when-considered">
          <p>
            <strong>SAINT®</strong> is usually considered for adults with major
            depressive disorder who have not gotten adequate relief from
            antidepressant medication and want the fastest option in the fewest
            clinic days. SAINT® is FDA-cleared for MDD in adults whose
            depression has not improved enough on prior antidepressant
            medication. Neuro Wellness treats adults 22 and older.{" "}
            <a href="/saint">Learn more about SAINT®</a>.
          </p>
          <p>
            <strong>Standard TMS</strong> is often the first brain-stimulation
            option after medication fails, especially when insurance coverage is
            a priority and a six-week schedule is manageable.{" "}
            <a href="/learn/saint-vs-accelerated-tms">
              SAINT® vs accelerated TMS
            </a>
            .
          </p>
          <p>
            <strong>SPRAVATO®</strong> is considered when rapid relief is needed
            and ongoing in-clinic dosing is acceptable. It is FDA-approved for
            treatment-resistant depression and for major depressive disorder
            with acute suicidal ideation.{" "}
            <a href="/learn/saint-vs-spravato">SAINT® vs SPRAVATO®</a>.
          </p>
          <p>
            <strong>IV ketamine</strong> is used off-label for rapid, short-term
            relief, often while waiting for a longer-term treatment to take
            hold. <a href="/learn/saint-vs-ketamine">SAINT® vs ketamine</a>.
          </p>
          <p>
            <strong>ECT</strong> remains one of the most effective treatments in
            psychiatry and is typically considered when other options have not
            worked or when rapid, high-intensity intervention is needed. It
            requires anesthesia and carries a known risk of memory problems.{" "}
            <a href="/learn/saint-vs-ect">SAINT® vs ECT</a>.
          </p>
        </div>
      </DecisionSection>

      {/* All under one roof */}
      <DecisionSection heading="All five under one roof at Neuro Wellness">
        <div id="one-roof">
          <p>
            Neuro Wellness TMS Centers of America offers SAINT®, standard TMS,
            SPRAVATO®, IV ketamine, and psychotherapy in the same Coral Springs
            clinic. That means a combined or sequential plan — for example,
            starting ketamine for interim relief while arranging a SAINT® week,
            or following SAINT® with SPRAVATO® maintenance — can happen under
            one care team without being sent elsewhere. ECT is not performed
            on-site; if it is the right option for you, we coordinate a
            referral.
          </p>
          <p>
            Neuro Wellness is a Verified SAINT® Provider in Coral Springs,
            Florida. SAINT® at Neuro Wellness starts at $25,000; coverage varies
            by plan, and we verify your benefits for free.{" "}
            <a href="/check-insurance-options">Check my insurance</a> or{" "}
            <a href="/am-i-a-candidate-screener">learn if it's a fit</a>.
          </p>
        </div>
      </DecisionSection>

      {/* Full comparison table */}
      <DecisionSection heading="Full comparison table">
        <div id="comparison-table">
          <div className="overflow-x-auto -mx-4 px-4">
            <table className="w-full min-w-[700px] border-collapse text-xs sm:text-sm">
              <thead>
                <tr className="border-b-2 border-border">
                  <th className="text-left py-3 pr-3 font-headings font-bold text-primary">
                    Treatment
                  </th>
                  <th className="text-left py-3 pr-3 font-headings font-bold text-primary">
                    FDA status
                  </th>
                  <th className="text-left py-3 pr-3 font-headings font-bold text-primary">
                    Time commitment
                  </th>
                  <th className="text-left py-3 pr-3 font-headings font-bold text-primary">
                    Time to response
                  </th>
                  <th className="text-left py-3 pr-3 font-headings font-bold text-primary">
                    Response / remission (cited)
                  </th>
                  <th className="text-left py-3 pr-3 font-headings font-bold text-primary">
                    Common side effects
                  </th>
                  <th className="text-left py-3 font-headings font-bold text-primary">
                    Cost / coverage
                  </th>
                </tr>
              </thead>
              <tbody className="text-foreground/85">
                {TREATMENTS.map((t) => (
                  <tr key={t.id} className="border-b border-border align-top">
                    <td className="py-3 pr-3 font-semibold text-primary">
                      {t.name}
                    </td>
                    <td className="py-3 pr-3">{t.fdaStatus}</td>
                    <td className="py-3 pr-3">{t.timeCommitment}</td>
                    <td className="py-3 pr-3">{t.timeToResponse}</td>
                    <td className="py-3 pr-3">{t.responseRemission}</td>
                    <td className="py-3 pr-3">{t.sideEffects}</td>
                    <td className="py-3">{t.costCoverage}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-muted-foreground italic mt-3">
            Study designs and populations vary across these figures. Individual
            results may vary. A consultation is required to determine whether
            any treatment is appropriate for you.
          </p>
        </div>
      </DecisionSection>

      {/* FAQ */}
      <DecisionSection heading="Frequently asked questions">
        <div id="faq" className="space-y-5">
          <div>
            <h3 className="font-headings text-base font-bold text-primary mb-1">
              Which depression treatment works fastest?
            </h3>
            <p className="text-foreground/85">
              SAINT® and IV ketamine both act within days rather than weeks. In
              the 2022 SAINT randomized trial, the average time to remission was
              2.6 days (Cole et al., Am J Psychiatry 2022;179(2):132–141). IV
              ketamine can produce relief within hours, though its use for
              depression is off-label and the effect often requires maintenance
              dosing. Standard TMS and ECT typically take weeks. Individual
              results may vary.
            </p>
          </div>
          <div>
            <h3 className="font-headings text-base font-bold text-primary mb-1">
              Can I combine SAINT with Spravato or ketamine?
            </h3>
            <p className="text-foreground/85">
              Yes. Neuro Wellness offers SAINT®, SPRAVATO®, and IV ketamine
              under one roof, so a combined or sequential approach is possible
              when clinically appropriate. Some patients use an accelerated TMS
              course for durable remission and add SPRAVATO® or ketamine for
              interim relief or maintenance. The specific plan is set at a
              consultation based on your diagnosis and history.
            </p>
          </div>
          <div>
            <h3 className="font-headings text-base font-bold text-primary mb-1">
              Which treatments does insurance usually cover?
            </h3>
            <p className="text-foreground/85">
              Standard TMS, deep TMS, SPRAVATO®, and ECT are generally covered
              by commercial plans and Medicare. SAINT® may be covered, but
              coverage varies by plan and is reviewed case by case through prior
              authorization; we verify your benefits for free. IV ketamine for
              depression is off-label and is generally not covered. Self-pay and
              financing options are available.
            </p>
          </div>
          <div>
            <h3 className="font-headings text-base font-bold text-primary mb-1">
              Which is best for me?
            </h3>
            <p className="text-foreground/85">
              There is no single best option — the right treatment depends on
              your diagnosis, how many antidepressants you have tried, how
              quickly you need relief, your tolerance for side effects, and your
              insurance. The interactive tool above lets you compare two or
              three treatments side by side, but a consultation with our
              clinical team is the only way to get a recommendation tailored to
              you. <a href="/am-i-a-candidate-screener">Learn if it's a fit</a>.
            </p>
          </div>
        </div>
      </DecisionSection>

      {/* Cited research */}
      <DecisionSection heading="Cited research">
        <div id="cited-research">
          <ol className="list-decimal pl-5 space-y-2 text-sm text-foreground/85">
            <li>
              Cole EJ, Phillips AL, Bentzley BS, et al. "Stanford Accelerated
              Intelligent Neuromodulation Therapy for treatment-resistant
              depression." <em>Am J Psychiatry</em> 2022;179(2):132–141.{" "}
              <a
                href="https://doi.org/10.1176/appi.ajp.2021.20101499"
                target="_blank"
                rel="noopener"
                className="text-accent hover:underline"
              >
                doi:10.1176/appi.ajp.2021.20101499
              </a>
            </li>
            <li>
              Janicak PG, Nahas Z, Lisanby SH, et al. "Durability of clinical
              benefit with TMS in TRD." <em>J Clin Psychiatry</em> 2010.
            </li>
            <li>
              Levkovitz Y, Isserles M, Padberg F, et al. "Efficacy and safety of
              deep TMS for MDD." <em>World Psychiatry</em> 2015;14(1):64–73.
            </li>
            <li>
              Daly EJ, Singh JB, Fedgchin M, et al. "Efficacy and safety of
              intranasal esketamine adjunctive to oral antidepressant in TRD."
              <em>Mol Psychiatry</em> 2019;24(4):601–612.
            </li>
            <li>
              Ivanova-Starkova O, et al. "Ketamine for depression: a
              meta-analysis." <em>J Affect Disord</em> 2019.
            </li>
            <li>
              UK ECT Review Group. "Efficacy and safety of ECT in depressive
              disorders." <em>Lancet</em> 2003;361(9360):799–808.
            </li>
          </ol>
        </div>
      </DecisionSection>

      {/* Safety block */}
      <DecisionSection>
        <div className="safety-block rounded-xl border border-border bg-muted/20 p-4 sm:p-5">
          <p className="text-sm text-foreground/85 leading-relaxed">
            SAINT® is not for everyone. It is contraindicated for people with
            non-removable magnetic-sensitive metal in or near the head, certain
            implanted devices, or increased intracranial pressure. Possible side
            effects include headache, fatigue, scalp discomfort and anxiety; TMS
            carries a small risk of seizure and, as with any depression
            treatment, a risk of worsening depression or suicidal thoughts. If
            you are in crisis, call or text 988.
          </p>
        </div>
      </DecisionSection>

      {/* Trademark footer */}
      <DecisionSection>
        <p className="text-xs text-muted-foreground">
          SAINT® is a registered trademark of Stanford University, exclusively
          licensed to Magnus Medical, Inc. NWCOA is not affiliated with Stanford
          University.
        </p>
      </DecisionSection>

      <InlineRelatedLinks
        heading="Explore Related Pages"
        links={[
          { title: "What Is SAINT® TMS?", href: "/saint" },
          { title: "SAINT® vs SPRAVATO®", href: "/learn/saint-vs-spravato" },
          { title: "SAINT® vs Ketamine", href: "/learn/saint-vs-ketamine" },
          { title: "SAINT® vs ECT", href: "/learn/saint-vs-ect" },
          { title: "SAINT® vs Deep TMS", href: "/learn/saint-vs-deep-tms" },
          {
            title: "Treatment-Resistant Depression Guide",
            href: "/learn/what-is-treatment-resistant-depression",
          },
          { title: "Am I a Candidate?", href: "/am-i-a-candidate-screener" },
          { title: "Check My Insurance", href: "/check-insurance-options" },
        ]}
      />
    </DecisionPageLayout>
  );
};

export default CompareTreatments;
